import { PORTAL_MARK_SVG } from '../assets/portalMark'

/**
 * The Portal OS app icon (24 Sep 2026).
 *
 * The mark in paper, sitting on a graphite tile that falls away from the top
 * left, lifted off it by a soft shadow. Tom picked this over a flat tile and
 * over an ink-on-paper one, from three rendered options.
 *
 * **Three wrong versions came first, and they were all the same mistake:** the
 * artwork was being taken from `brand.lockup` — the mark WITH "Portal
 * Productions" beside it. Squeezed into a square that becomes four-point type;
 * cropped back to the mark by scanning pixel columns it becomes a fifty-pixel
 * fragment enlarged to 512; and on Android, where every icon is masked to the
 * launcher's shape, it arrived on Shilpa's home screen reading "Por".
 *
 * The fix was not a better algorithm. It was the right file. assets/portalMark
 * holds the mark on its own, as vector, inside the bundle — so there is no
 * fetch, no CORS (a canvas that has loaded a cross-origin image is tainted and
 * `toDataURL` throws), no rasterising a thumbnail, and no resolution ceiling.
 *
 * The rendering below was checked against the approved image in a real browser
 * before it shipped, rather than assumed to match.
 */

const INK = '#0b0b0a'
const PAPER = '#f4f1ea'

/** Share of the tile the mark fills. It has its own rounded corners, so it
    reads as an object on the tile rather than artwork bleeding off it — and at
    52% it sits well inside the safe zone Android's launcher masks respect. */
const FILL = 0.52

/** The mark as an image at exactly `size`, in `colour`.

    The width and height are written into the SVG rather than left to the
    drawing call: an `<img>` rasterises an SVG once, at the size the file
    declares, so a file that declares a small size is a small bitmap however
    large it is later drawn. This is the whole reason the earlier icons were
    soft. */
function markImage(size: number, colour: string): Promise<HTMLImageElement | null> {
  const svg = PORTAL_MARK_SVG.replace(/#333/g, colour).replace(
    /<svg /,
    `<svg width="${size}" height="${size}" `,
  )
  /* A data URI rather than a blob URL: no object to revoke, and no chance of
     revoking it before the decode has finished. */
  const src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`

  return new Promise((resolve) => {
    const image = new Image()
    image.onload = () => resolve(image)
    image.onerror = () => resolve(null)
    image.src = src
  })
}

/** PORTAL over OS in the studio's own type — only if the mark cannot be drawn. */
function textTile(size: number): string {
  const canvas = document.createElement('canvas')
  canvas.width = size
  canvas.height = size
  const context = canvas.getContext('2d')
  if (!context) return ''
  const unit = size / 180
  context.fillStyle = INK
  context.fillRect(0, 0, size, size)
  context.fillStyle = PAPER
  context.textAlign = 'center'
  context.textBaseline = 'middle'
  context.font = `500 ${27 * unit}px Jost, Helvetica, Arial, sans-serif`
  context.fillText('PORTAL', size / 2, size / 2 - 17 * unit)
  context.font = `300 ${27 * unit}px Jost, Helvetica, Arial, sans-serif`
  context.fillText('OS', size / 2, size / 2 + 19 * unit)
  return canvas.toDataURL('image/png')
}

/** A square PNG data URL at `size`, or '' if nothing could be drawn. */
export async function appIconAt(size: number): Promise<string> {
  if (typeof document === 'undefined') return ''

  try {
    const canvas = document.createElement('canvas')
    canvas.width = size
    canvas.height = size
    const context = canvas.getContext('2d')
    if (!context) return ''

    /* The tile. Light falls from the top left and runs out before the bottom
       right corner, which is what stops it reading as a flat sticker. */
    const light = context.createRadialGradient(
      size * 0.28,
      size * 0.2,
      0,
      size * 0.28,
      size * 0.2,
      size * 1.2,
    )
    light.addColorStop(0, '#23231f')
    light.addColorStop(0.45, '#131311')
    light.addColorStop(1, INK)
    context.fillStyle = light
    context.fillRect(0, 0, size, size)

    const side = Math.round(size * FILL)
    const offset = Math.round((size - side) / 2)
    const image = await markImage(side, PAPER)
    if (!image) return textTile(size)

    /* Three passes: the shadow beneath, a hairline of light along the top
       edge, then the mark itself with no shadow so its edges stay crisp. */
    const unit = size / 512
    context.shadowColor = 'rgba(0,0,0,0.55)'
    context.shadowBlur = 14 * unit
    context.shadowOffsetY = 6 * unit
    context.drawImage(image, offset, offset, side, side)

    context.shadowColor = 'rgba(255,255,255,0.10)'
    context.shadowBlur = 0
    context.shadowOffsetY = 1 * unit
    context.drawImage(image, offset, offset, side, side)

    context.shadowColor = 'transparent'
    context.shadowBlur = 0
    context.shadowOffsetY = 0
    context.drawImage(image, offset, offset, side, side)

    return canvas.toDataURL('image/png')
  } catch (error) {
    /* Never worth a broken page over an icon. */
    console.warn('[app icon] could not be drawn', error)
    return ''
  }
}
